import { isValid } from 'date-fns';
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import {
	Button,
	Form,
	Modal,
	OverlayTrigger,
	Table,
	Tooltip
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import { withTranslation } from "react-i18next";
import { AiOutlineClose } from "react-icons/ai";
import { BsInfoCircle } from "react-icons/bs";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { FiDelete } from "react-icons/fi";
import { useNavigate } from 'react-router-dom';
import { LoaderContext } from "../../../../App";
import PaginationComponent from "../../../../components/Pagination/Pagination";
import ReportingPeriod from '../../../../components/Regulation/ReportingPeriod';
import { DEFAULT_ITEMS_PER_PAGE, LIST_DEF_SORT, LS_AUTHORITY_KEY, LS_COMPANY_INFO_KEY, LS_SELECTED_MAT_ISSUE_YEAR, MaterialIssueVersionStatus, SORT_ORDER_DESC, localeMap } from "../../../../core/constants/common";
import FormService from "../../../../core/service/FormService";
import MatAssessmentService from '../../../../core/service/MatAssessmentService';
import MaterialIssueService from '../../../../core/service/MaterialIssueService';
import Utils from "../../../../core/utilities/Utils";
import GRI_pic from "../../../../images/GRI.jpg";
import "../../../Common/Forms/Forms.scss";


const CardType = {
	FORM_SELECTION: 1,
	FORM_DETAILS: 2,
	PERMISSION_CONFIGURATION: 3,
	CONFIRMATION: 4
};

function MatAssessmentModal({ t, duplicate, onHide, onSave, enablePermCard, id }) {
	const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
	const companyId = lsData.profile.company.id;
	const currentYear = new Date().getFullYear();
	const selectedYear = localStorage.getItem(LS_SELECTED_MAT_ISSUE_YEAR) ?? null;

	const { setLoaderDisplay } = useContext(LoaderContext);
	const [show, setShow] = useState(true);
	const [activeCard, setActiveCard] = useState(
		(enablePermCard) ? CardType.PERMISSION_CONFIGURATION : CardType.FORM_DETAILS
	);
	const [showModalTitle, setShowModalTitle] = useState(true);
	const [itemsTotalCount, setItemTotalCount] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE - 2);
	const [currentPage, setCurrentPage] = useState(1);
	const [forms, setForms] = useState([]);

	const [dataToSave, setDataToSave] = useState({
		period: `${selectedYear ? selectedYear : currentYear}-01-01`,
		lastSubmissionDate: null,
		formPermissions: [],
		assessmentForm: null,
		regulation: null,
	});
	const [formSkeleton, setFormSkeleton] = useState(null);
	const [stakeholders, setStakeholders] = useState([]);
	const [detailsValidated, setDetailsValidated] = useState(false);
	const [permsValidated, setPermsValidated] = useState(false);
	const [selectedSection, setSelectedSection] = useState(null);
	const [sectionVisibility, setSectionVisibility] = useState({});
	const [isIssuesFinalized, setIsIssuesFinalized] = useState(false);
	const isAnyFormPublished = forms.some(form => form.status === 'PUBLISHED');
	const [loadModal, setLoadModal] = useState(false);
	const navigate = useNavigate();
	const [duplicateSurvey, setDuplicateSurvey] = useState({});
	const [stakeholderData, setStakeholderData] = useState({});

	useEffect(() => {
		if (!id) {
			fetchInitData(selectedYear ? selectedYear : currentYear);
		}
	}, []);

	useEffect(() => {
		if (!id) {
			return;
		} else {
			getAssessment();
		}
	}, [id]);

	const getForms = (pageNumber, itemsPerPageVal, frequency = 'MONTHLY') => {
		if (pageNumber > 0) {
			pageNumber = pageNumber - 1;
		} else {
			setCurrentPage(1);
		}
		const perPage = itemsPerPageVal > 0 ? itemsPerPageVal : itemsPerPage;
		const sort = `${LIST_DEF_SORT}, ${SORT_ORDER_DESC}`;
		FormService.getForms(
			pageNumber,
			perPage,
			'',
			sort,
			'',
			true,
			true,
			true,
			null,
			'SURVEY'
		)
			.then((response) => {
				setItemTotalCount(parseFloat(response.headers["x-total-count"]));
				setForms([...response.data]);
			}).catch((error) => { });
	};

	const getFormPreview = (id, checkForSurveyCreation = false, period = null) => {
		if (!id) {
			setLoaderDisplay(false);
			return;
		}
		const requests = [FormService.getFormPreview(id)];
		if (checkForSurveyCreation && period) {
			requests.push(FormService.searchAssessment(id, period));
		}
		Promise.all(requests).then(res => {
			setLoaderDisplay(false);
			const formPreviewData = res[0].data;
			setFormSkeleton(formPreviewData);
			if (checkForSurveyCreation && period) {
				const surveyData = res[1].data;
				setDuplicateSurvey({ id: surveyData?.id ?? 0 });
			}
		}).catch(err => {
			setLoaderDisplay(false);
		});
	};

	const getStakeholders = (year, isModal) => {
		const selectedYear = year ?? currentYear;
		MatAssessmentService.getChildrenStakeholders(selectedYear, companyId).then(res => {
			if (res.status !== 200) return;

			setupStakeHolderData(res.data);
			setLoaderDisplay(false);
			if (isModal) {
				setLoadModal(true);
			}
		}).catch(err => {
			setLoaderDisplay(false)
		});
	};

	const getAssessment = () => {
		setLoaderDisplay(true)
		FormService.getAssessment(id).then(res => {
			if (res.status !== 200) return;

			// Fetch all sections for the forms and stakeholders
			const assessmentForm = res.data.assessmentForm;
			getFormPreview(assessmentForm.id);
			const date = new Date(res.data.period);
			getStakeholders(date.getFullYear(), true);
			// Populate form data
			const perms = [];
			res.data.formPermissions?.forEach(p => {
				const perm = {
					stakeholders: [],
					section: p.section
						? { id: p.section.id, title: p.section.title }
						: { id: -1, title: t('CompleteForm.label') }
				};
				p.stakeholders?.forEach(s => perm.stakeholders.push({ id: s.id }));
				perms.push(perm);
			});
			setDataToSave({
				period: res.data.period,
				lastSubmissionDate: res.data.lastSubmissionDate,
				formPermissions: perms,
				assessmentForm: {
					id: assessmentForm.id,
					name: assessmentForm.name,
					description: assessmentForm.description
				},
				regulation: assessmentForm.regulation,
				frequency: res.data.frequency,
				calendarEventRef: res.data.calendarEventRef
			});
			let activeCardData = (enablePermCard) ? CardType.PERMISSION_CONFIGURATION : CardType.FORM_DETAILS;
			setActiveCard(activeCardData);

			if ((activeCardData === CardType.PERMISSION_CONFIGURATION
				|| activeCardData === CardType.FORM_DETAILS
				|| activeCardData === CardType.CONFIRMATION)
				&& !enablePermCard) {
				setShowModalTitle(true);
			} else {
				setShowModalTitle(false);
			}
			// setLoadModal(true);
		}).catch(err => {
			setLoaderDisplay(false)
		});

	};

	const exitModal = () => {
		setShow(false);
		onHide();
	};

	const onFormSelection = (form, regulation) => {

		setDataToSave({
			...dataToSave,
			assessmentForm: {
				id: form.id,
				name: form.name,
				description: form.description
			},
			regulation: regulation
		});
		setActiveCard(CardType.FORM_DETAILS);
		getFormPreview(form.id);
		getStakeholders(selectedYear ? selectedYear : currentYear, false);
	};

	const onBack = () => {
		const prevCard = activeCard - 1;
		if (activeCard === CardType.PERMISSION_CONFIGURATION) {
			setActiveCard(CardType.FORM_DETAILS);
		} else {
			setActiveCard(prevCard ?? CardType.FORM_SELECTION);
		}
	};
	const onNext = () => {
		if (activeCard === CardType.FORM_DETAILS
			&& (!dataToSave.lastSubmissionDate || !dataToSave.period || !dataToSave.assessmentForm?.id)) {
			setDetailsValidated(true);
			return
		}
		if (activeCard === CardType.PERMISSION
			&& dataToSave.supplierIds?.length === 0) {
			setPermsValidated(true);
			return;
		}
		if (activeCard === CardType.PERMISSION_CONFIGURATION
			&& dataToSave.formPermissions?.length === 0) {
			setPermsValidated(true);
			return;
		}
		setPermsValidated(false);
		const nextCard = activeCard + 1;
		if (activeCard === CardType.FORM_DETAILS) {
			setActiveCard(CardType.PERMISSION_CONFIGURATION);
		} else {
			setActiveCard(nextCard > CardType.CONFIRMATION ? CardType.CONFIRMATION : nextCard);
		}
	};

	const setLastSubmissionDate = (date) => {
		setDataToSave({
			...dataToSave,
			lastSubmissionDate: moment(date).format('YYYY-MM-DD')
		});
	};

	const saveAssessment = () => {
		createOrUpdatAssessment();
	};

	const createOrUpdatAssessment = () => {
		delete dataToSave.supplierIds;
		delete dataToSave.regulation;
		delete dataToSave.assessmentForm.name;
		delete dataToSave.assessmentForm.description;
		let req;
		let payload = dataToSave;
		if (!duplicate && id > 0) {
			req = MatAssessmentService.updateAssessment(id, payload);
		} else {
			req = MatAssessmentService.createAssessment(payload);
		}

		setLoaderDisplay(true);
		req.then(res => {
			setLoaderDisplay(false);
			if (res.status !== 200) return;

			// const successMessage = !duplicate && id > 0 ? "Success" : t('MatAssessmentCreationSuccessMessage.label');
			// toast.success(successMessage, {
			// 	position: toast.POSITION.TOP_RIGHT,
			// });

			exitModal();
			onSave();
		}).catch(err => {
			setLoaderDisplay(false);
		});
	};


	const onStakeholderCheck = (id) => {
		const perms = JSON.parse(JSON.stringify(dataToSave.formPermissions));
		const permIndex = perms.findIndex(p => p.section?.id === selectedSection?.id);
		if (permIndex >= 0) {
			const existingHolders = perms[permIndex].stakeholders;
			const holderIndex = existingHolders?.findIndex(g => g.id === id);
			if (holderIndex >= 0) {
				perms[permIndex].stakeholders.splice(holderIndex, 1);
				if (perms[permIndex].stakeholders?.length == 0) {
					perms.splice(permIndex, 1);
				}
			} else {
				perms[permIndex].stakeholders.push({ id: id });
			}
		} else {
			perms.push({
				section: {
					id: selectedSection?.id,
					title: selectedSection?.title,
				},
				stakeholders: [{ id: id }]
			});
		}
		setDataToSave({
			...dataToSave,
			formPermissions: perms
		});
	};

	const isPermittedForStakeholder = (holderId) => {
		const index = dataToSave.formPermissions?.findIndex(p => p.section?.id === selectedSection.id);
		if (index < 0) return false;

		return dataToSave.formPermissions[index].stakeholders?.findIndex(s => s.id === holderId) >= 0;
	}

	const onPermissionDelete = (sectionId) => {
		const perms = JSON.parse(JSON.stringify(dataToSave.formPermissions));
		const permIndex = perms.findIndex(p => p.section?.id === sectionId);
		if (permIndex < 0 || !perms[permIndex]) return;

		perms.splice(permIndex, 1);
		setDataToSave({
			...dataToSave,
			formPermissions: perms
		});
	};

	const hasPermissionForSection = (sectionId) => {
		const index = dataToSave?.formPermissions?.findIndex(p => p.section.id === sectionId);
		if (index < 0) return false;

		return dataToSave?.formPermissions[index]?.stakeholders?.length > 0;
	};

	const getStakeholderNames = (holderList) => {
		let names = '';
		for (let i = 0; i < holderList.length; i++) {
			const holder = holderList[i];
			const holderName = stakeholders?.find(h => h.id === holder.id)?.name ?? '';
			names += holderName;
			if (i !== holderList.length - 1) {
				names += ', ';
			}
		}
		return names;
	};

	const expandOrCollapseSections = (section) => {
		let visibility = { ...sectionVisibility };
		section?.subSections?.forEach(s => {
			visibility[s.id] = visibility[s.id] ? false : true;
		});
		visibility[section.id] = visibility[section.id] ? false : true;
		setSectionVisibility(visibility);
	};

	const formSelectionCardToRender = () => {
		return (
			<div className={activeCard === CardType.FORM_SELECTION ? '' : 'd-none'}>
				<div>
					<h4>{t('FormSelectionCardHeader.label')}</h4>
					<p>{t('FormSelectionCardText.label')}</p>
				</div>
				<div className="assessmentCard_form">
					<div className="row m-0 justify-content-center Choose_formBox_02 ">
						{!isAnyFormPublished ? (
							<div>
								<h6>{t('NoPublishedForms.label')}</h6>
							</div>
						) : (

							forms?.map((form, index) => {
								if (form.status === 'PUBLISHED') {
									return (
										<div
											className={`col-md-3 p-1 ${form.id === dataToSave.assessmentForm?.id ? 'selected-form' : ''
												}`}
											key={`formCard-${index + 1}`}
										>
											<div
												className="card Assessment_card_Singleview"
												onClick={() => onFormSelection(form, form.regulation)}
											>
												<div className="card-body">
													<div className="assess_pic">
														<img
															src={
																form.logo?.id > 0
																	? FormService.getLogo(form.logo?.id, form.id)
																	: GRI_pic
															}
															alt="Form Logo"
														/>
													</div>
													<h6>{form.name}</h6>
													<p>{form.description}</p>
													<ul>
														<li>
															<p className="update">{t('UpdatedOn.label')}</p>
															<p className="date">
																{moment(form?.lastModifiedDate).format('MMM DD, YYYY')}
															</p>
														</li>
														<li>
															<p>
																{form?.sectionCount} {t('Sections.label')}
															</p>
															<p>
																{form?.subSectionCount} {t('SubSections.label')}
															</p>
															<p>
																{form?.questionCount} {t('Questions.label')}
															</p>
														</li>
													</ul>
												</div>
											</div>
										</div>
									);
								} else {
									return null;
								}
							})
						)
						}
						<div className="p-2 d-flex align-items-center justify-content-end">
							{isAnyFormPublished &&
								<PaginationComponent
									itemsTotalCount={itemsTotalCount}
									itemsPerPage={itemsPerPage}
									currentPage={currentPage}
									setCurrentPage={setCurrentPage}
									alwaysShown={true}
									setItemsPerPage={setItemsPerPage}
									pageClicked={(pageNum, perPage) => {
										getForms(pageNum, perPage, dataToSave.frequency);
									}}
								/>
							}
						</div>
					</div>
					<div className="col-md-12 p-3">
						<Button variant="secondary " className="px-4" onClick={() => exitModal()}>
							{t("Cancel.label")}
						</Button>
					</div>
				</div>
			</div >
		);
	};

	const renderFooterActions = () => {
		return (
			<div className="text-center p-3 pt-2">
				{(!enablePermCard) && (
					<>
						{
							activeCard !== CardType.FORM_DETAILS &&
							<Button
								variant="secondary"
								className="px-4 me-2"
								onClick={() => onBack()}
							>
								{t("Back.label")}
							</Button>
						}
						<Button
							variant="secondary"
							className="px-4 me-2"
							onClick={() => exitModal()}
						>
							{t("Cancel.label")}
						</Button>
						{
							(id > 0 || (isIssuesFinalized && !duplicateSurvey.id && stakeholderData?.totalWeigthage === 100 && stakeholders.length > 0)) &&
							<Button
								variant="primary"
								className="px-4"
								onClick={() => onNext()}>
								{t("Next.label")}
							</Button>
						}						
					</>
				)}

				{(enablePermCard) && (
					<Button variant="primary "
						className="px-4"
						onClick={() => saveAssessment()}>
						{t("Save.label")}
					</Button>
				)}
			</div>
		);
	};

	const detailsCardToRender = () => {
		return (
			<div className={activeCard === CardType.FORM_DETAILS ? '' : 'd-none'}>
				<div>
					<h4>{t('MaterialityFormDetailsCard.label')}</h4>
				</div>
				<div className="assessmentCard_form">
					<div className="row m-0 justify-content-center FormDetailsCard_002">
						<div className="col-md-6 p-2 text-start 1">
							<Form noValidate validated={detailsValidated}>
								<div className="row m-0 mb-3">
									<div className="col-md-12 p-2">
										<label>{t('Year.label')}</label>
										<ReportingPeriod
											disabled={id > 0}
											defaultFrequency='ANNUAL'
											defaultPeriod={id > 0 ? dataToSave.period : selectedYear ? `${selectedYear}-01-01` : null}
											valueChangeCallback={(period) => {
												fetchInitData(period.year);
												setDataToSave({ ...dataToSave, period: `${period.year}-01-01` });
											}
											} />
									</div>
								</div>
								{
									stakeholders?.length > 0
									// stakeholders?.every(stakeholder => stakeholder?.groups?.length > 0) 
									&& (id || (isIssuesFinalized && !duplicateSurvey.id && stakeholderData?.totalWeigthage === 100)) &&
									<div className="row m-0 mb-3">
										<div className="col-md-12 p-2">
											<label className='mb-1'>{t('Expiry.label')}</label>
											<div className="pb-2">
												<DatePicker
													selected={
														dataToSave.lastSubmissionDate && isValid(new Date(dataToSave.lastSubmissionDate))
															? new Date(dataToSave.lastSubmissionDate)
															: null
													}
													onChange={(date) => setLastSubmissionDate(date)}
													className="form-control"
													required
													locale={localeMap[t("datePicker.locale")]}
													dateFormat="dd/MM/yyyy"
													minDate={new Date()}
												/>
											</div>
										</div>
									</div>
								}
							</Form>
							{renderLatestFinalizedForm()}
							<div className="pb-5"></div>
							{renderFooterActions()}
						</div>
					</div>
				</div>
			</div>
		);
	};

	const permissionConfigurationCardToRender = () => {
		return (
			<div className={activeCard === CardType.PERMISSION_CONFIGURATION ? '' : 'd-none'}>
				<div>
					{/* {enablePermCard ? (
						<h4>{t('PermissionConfigHeaders.label')}</h4>
					) : (
						<h4>{t('MatPermissionConfigHeader.label')}</h4>
					)}
					<p>{t('PermissionConfigText.label')}</p> */}
					<h4>{t('PermissionConfigText.label')}</h4>
				</div>
				<div className="assessmentCard_form">
					<div className="row m-0">
						<div className="col-md-7 p-2 text-start ConfigurePermissionsTable">
							{
								permsValidated &&
								<div className="alert alert-danger m-0 p-1">
									<span>{t('AddOnePermission.label')}</span>
									<span className="cursor"><AiOutlineClose onClick={() => setPermsValidated(false)} /></span>
								</div>
							}
							<div className="card">
								<Table>
									<thead>
										<tr>
											<th>{t('SectionOrSubSection.label')}</th>
											<th>{t('StakeHolders.label')}</th>
											<th></th>
										</tr>
									</thead>
									<tbody>
										<tr className={`cursor ${selectedSection?.id === -1 ? 'bg-selected-row' : ''}`}>
											<td onClick={() => setSelectedSection({ id: -1, title: t('CompleteForm.label') })}>{t('CompleteForm.label')}</td>
											<td>{renderSelectedStakeholdersForSection(-1)}</td>
											<td>
												{
													hasPermissionForSection(-1) &&
													<span className="cursor mx-1">
														<FiDelete onClick={() => onPermissionDelete(-1)} />
													</span>
												}
											</td>
										</tr>
										{formSkeleton?.sections?.length > 0 && renderSectionRows(formSkeleton.sections)}
									</tbody>
								</Table>
							</div>
						</div>
						<div className="col-md-5 p-2 text-start">
							{(selectedSection?.id > 0 || selectedSection?.id === -1) && renderPermissionModal()}
						</div>
						{renderFooterActions()}
					</div>
				</div>
			</div>
		);
	};

	const getEmailsOfEntitiesInStakeholder = (holder) => {
		let emails = [];
		holder?.groups?.forEach(g => {
			emails.push(<div>{g.email}</div>);
		});
		return emails;
	};

	const renderPermissionModal = () => {
		return (
			<div className="AllSection_Viewcard card">
				<div className="Form_Section_groups">
					<div className="Form_Section_groups_head d-flex justify-content-between">
						<span>{t('IdentifiedStakeholderGroups.label')}</span>
						<span className="cursor" onClick={() => setSelectedSection(null)}><AiOutlineClose /></span>
					</div>
					<div className="Form_Section_groups_body 1">
						{/* <div className="d-flex justify-content-between text-muted">
							<span>{t('StakeHolders.label')}</span>
						</div> */}
						<ul>
							{
								stakeholders?.sort((a, b) => a.name.localeCompare(b.name)).map(holder => {
									return (
											<OverlayTrigger
												placement="auto"
												overlay={<Tooltip id={`groupEmail-${holder.id}`} className="text-start"><div>{getEmailsOfEntitiesInStakeholder(holder)}</div></Tooltip>}>
												<li key={`group-${holder.id}`} className="d-flex align-items-baseline w-100">
													<div className="form-check">
														<input className="form-check-input" type="checkbox"
															id={`group-input-${holder.id}`}
															checked={isPermittedForStakeholder(holder.id)}
															onChange={() => onStakeholderCheck(holder.id)} />
														<label className="form-check-label" for={`group-input-${holder.id}`}>
															<span>{Utils.getTextBasedProfilePic(holder.name)}</span> {holder.name}
														</label>
													</div>
												</li>
											</OverlayTrigger>
									);
								})
							}
						</ul>
					</div>
				</div>
			</div>
		);
	};

	const confirmationCardToRender = () => {
		return (
			<div className={`assessmentForm_Confirmation ${activeCard === CardType.CONFIRMATION ? '' : 'd-none'}`}>
				<div>
					<h4>{t('confirmation.label')}</h4>
				</div>
				<div className="assessmentCard_form">
					<div className="row m-0">
						<div className="col-md-9 p-2 text-start Permissions_ConfirmationBox">
							<div>
								<div className="mb-1">{t('AssignedSecStakeGroups.label')}</div>
								<div className="card">
									<Table className="mb-0">
										<thead>
											<tr>
												<th>{t('SectionOrSubSection.label')}</th>
												<th>{t('StakeHolders.label')}</th>
											</tr>
										</thead>
										<tbody>
											{
												dataToSave?.formPermissions?.map(perm => {
													return (
														<tr key={`sharedPermission-${perm.id}`}>
															<td>
																{perm.section.title}
															</td>
															<td>
																{getStakeholderNames(perm?.stakeholders)}
															</td>
														</tr>
													);
												})
											}

										</tbody>
									</Table>
								</div>
							</div>
						</div>

						<div className="col-md-3 p-2 text-start">
							<div className="mb-3">
								<span>{t('Year.label')}</span>
								<div>
									<b>{Utils.reportingPeriodToReadableText(dataToSave.period, 'ANNUAL', t)}</b>
								</div>
							</div>
							<div>
								<span>{t('Expiry.label')}</span>
								<div>
									<b>{moment(dataToSave.lastSubmissionDate).format("MMM DD, YYYY")}</b>
								</div>
							</div>

						</div>

					</div>
					<div className="text-center p-3">
						<Button variant="secondary " className="px-4 me-2"
							onClick={() => onBack()}>
							{t("Back.label")}
						</Button>
						<Button variant="secondary " className="px-4 me-2"
							onClick={() => exitModal()}>
							{t("Cancel.label")}
						</Button>
						<Button variant="primary " className="px-4"
							onClick={() => saveAssessment()}>
							{t("Confirm.label")}
						</Button>
					</div>
				</div>
			</div >
		);
	};
	const wizardToRender = () => {
		return (
			<div className="wizard_lineBox">
				{
					isAnyFormPublished &&
					<ul>
						<li className={getWizardBubbleClass(CardType.FORM_SELECTION)}><span></span></li>
						<li className={getWizardBubbleClass(CardType.FORM_DETAILS)}><span></span></li>
						<li className={getWizardBubbleClass(CardType.PERMISSION_CONFIGURATION)}><span></span></li>
						<li className={getWizardBubbleClass(CardType.CONFIRMATION)}><span></span></li>
					</ul>
				}
			</div>
		);
	};

	const getWizardBubbleClass = (cardType) => {
		return activeCard > cardType
			? 'done'
			: (activeCard === cardType ? 'select' : '');
	};

	const renderSectionRows = (sections, depth = 0) => {
		let level = 1;
		return (
			<>
				{
					sections?.sort((a, b) => a.title.localeCompare(b.title)).map((section, index) => {
						return (
							<>
								{
									(depth === 0 || sectionVisibility[section.id] === true) &&
									<tr key={`sectionRow-${section.id}-${index}`}
										className={selectedSection?.id === section.id ? 'bg-selected-row' : ''}>
										<td>
											<div className="Section_Sub-section_dropdown">
												<span className="dropdown_icon" style={{ marginLeft: `${depth * 25}px` }}>
													{
														section?.subSections?.length > 0 &&
														<>
															{
																sectionVisibility[section.id] === true
																	? <FaChevronUp className="cursor" onClick={() => expandOrCollapseSections(section)} />
																	: <FaChevronDown className="cursor" onClick={() => expandOrCollapseSections(section)} />
															}
														</>
													}
												</span>
												<p className='cursor m-0'
													onClick={() => setSelectedSection({ id: section.id, title: section.title })}>{section.title}
												</p>
											</div>


										</td>
										<td>{renderSelectedStakeholdersForSection(section.id)}</td>
										<td>
											{
												hasPermissionForSection(section.id) &&
												<span className="cursor mx-1">
													<FiDelete onClick={() => onPermissionDelete(section.id)} />
												</span>
											}
										</td>
									</tr>
								}
								{
									section?.subSections?.length > 0 &&
									renderSectionRows(section?.subSections, depth + level)
								}
							</>
						);
					})
				}
			</>
		);
	};

	const renderSelectedStakeholdersForSection = (sectionId) => {
		return (
			<div className="Group_id_Box">
				{
					dataToSave.formPermissions?.find(p => p?.section?.id === sectionId)?.stakeholders?.map(holder => {
						return (
							<span>
								{Utils.getTextBasedProfilePic(stakeholders?.find(g => g.id === holder.id)?.name)}
							</span>
						);
					})
				}
			</div>
		)
	};

	const renderLatestFinalizedForm = () => {
		const renderCard = () => (
			<div className='row d-flex justify-content-center d-none'>
				<div className='col-6'>
					<div className="card Assessment_card_Singleview">
						<div className="card-body">
							<div className="assess_pic">
								<img src={GRI_pic} alt="Form Logo" />
							</div>
							<h6 className='px-2'>{dataToSave?.assessmentForm?.name}</h6>
						</div>
					</div>
				</div>
			</div>
		);

		let content = [];
		if (duplicateSurvey?.id > 0) {
			content.push(
				<span><div className='Info_01pop'>
					<BsInfoCircle />
					{t('DuplicateSurveyHelper.label')}					
				</div><br></br></span>
			);
		} else if (stakeholders.length == 0) {
			content.push(
				<span><div className='Info_01pop'>
					<BsInfoCircle />
					{t('NoStakeholderGroupsAvailable.Part1')}
					<a href='#' onClick={() => navigate(`/company_admin/dmat/stakeholders`)}>
						{t('NoStakeholderGroupsAvailable.Part2')}
					</a>
					{t('NoStakeholderGroupsAvailable.Part3')}
				</div><br></br></span>
			);
		} else if (content.length == 0 && id) {
			content = (
				<>
					{content}
					{renderCard()}
				</>
			);
		}
		if (!id && stakeholderData?.totalWeigthage < 100) {
			content.push(
				<span><div className='Info_01pop'>
					<BsInfoCircle />
					{t('LowStakeholderGroupsWeightage.Part1')}
					<a href='#' onClick={() => navigate(`/company_admin/dmat/stakeholders`)}>
						{t('LowStakeholderGroupsWeightage.Part2')}
					</a>
					{t('LowStakeholderGroupsWeightage.Part3')}
				</div><br></br></span>
			);
		} 
		if (!id && stakeholderData?.invalidGroupCount > 0) {
			content.push(
				<span><div className='Info_01pop'>
					<BsInfoCircle />
					{t('StakeholderGroupsInvalid.Part1')}
					<a href='#' onClick={() => navigate(`/company_admin/dmat/stakeholders`)}>
						{t('StakeholderGroupsInvalid.Part2')}
					</a>
					{t('StakeholderGroupsInvalid.Part3')}
				</div><br></br></span>
			);
		} 
		if (!id && !isIssuesFinalized) {
			content.push(
				<div className='Info_01pop'>
					<BsInfoCircle />
					{t('NoIssuesFinalized.Part1')}
					<a href='#' onClick={() => navigate(`/company_admin/dmat/issues`)}>
						{t('NoIssuesFinalized.Part2')}
					</a>
					{t('NoIssuesFinalized.Part3')}
				</div>
			);
		}

		// If none of the previous conditions match, render the card only
		if (content.length == 0) {  // Ensure we only proceed if no content has been set
			content = renderCard();
		}

		return content;
	};

	const fetchInitData = (year) => {
		setLoadModal(true);
		setLoaderDisplay(true);
		setDuplicateSurvey({ id: 0});
		const selectedYear = year ?? currentYear;
		const req1 = MatAssessmentService.getChildrenStakeholders(selectedYear, companyId);
		const req2 = MaterialIssueService.getLatestIssueVersion(companyId, year, MaterialIssueVersionStatus.FINALIZED);
		Promise.all([req1, req2]).then(res => {
			const stakeholdersRes = res[0].data;
			const latestVersionRes = res[1].data;
			setupStakeHolderData(stakeholdersRes);
			if (latestVersionRes) {
				setIsIssuesFinalized(true);
				setDataToSave(d => {
					d.assessmentForm = {
						id: latestVersionRes?.formRef,
						name: `Materiality Survey - ${latestVersionRes.year} Version ${latestVersionRes.versionNumber}`,
					};
					d.regulation = null;
					return d;
				});
				setActiveCard(CardType.FORM_DETAILS);
				getFormPreview(latestVersionRes.formRef, true, `${year}-01-01`);
			} else {
				setIsIssuesFinalized(false);
				setLoaderDisplay(false);
			}
		}).catch(err => {
			setLoaderDisplay(false);
		});
	};

	const setupStakeHolderData = (groups) => {
		setStakeholders(groups);
		let weightage = 0, stakeholderCount = 0, invalidGroups = 0;
		groups?.forEach(g => {
			weightage += g.weightage ?? 0;
			if (g.groups?.length > 0) {
				stakeholderCount += g.groups?.length;
			} else {
				invalidGroups++;
			}
		});
		setStakeholderData({
			totalWeigthage: weightage,
			stakeholderCount: stakeholderCount,
			invalidGroupCount: invalidGroups
		});
	};

	return (
		<>
			{loadModal && <Modal
				show={show}
				onHide={() => exitModal()}
				size="xl"
				aria-labelledby="contained-modal-title-vcenter"
				centered >
				<Modal.Header closeButton>
					<Modal.Title as="h5">

						{(id <= 0 || id == null) && activeCard === CardType.FORM_SELECTION ? (
							t("CreateMaterialityAssessment.label")
						) : activeCard === CardType.FORM_SELECTION && (
							t("UpdateMaterialityAssessment.label")

						)}
					</Modal.Title>
					{showModalTitle && (
						<Modal.Title as="h5">{
							(() => {
								// Prevent ReDoS: use safer extraction method
								const name = dataToSave?.assessmentForm?.name;
								if (!name || typeof name !== 'string' || name.length > 500) {
									return name;
								}
								// Extract first 4 digits using match() - safer than replace with backtracking regex
								const match = name.match(/\d{4}/);
								return match ? match[0] : name;
							})()
						}</Modal.Title>
					)}
				</Modal.Header>
				<Modal.Body>
					<div className="assessment_modal_card">
						<div className="">
							{!enablePermCard && wizardToRender()}
							{formSelectionCardToRender()}
							{detailsCardToRender()}
							{permissionConfigurationCardToRender()}
							{confirmationCardToRender()}
						</div>
					</div>
				</Modal.Body>
			</Modal>}
		</>
	);
}

export default withTranslation()(MatAssessmentModal);
